import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  startAppDataManager,
  stopAppDataManager,
  selectDrivers,
  selectVehicles,
  selectRides,
  selectShifts,
  selectCustomers,
  selectIsManagerActive,
  selectAllLoadingStates,
  selectAllErrors,
  selectDriversState,
  selectVehiclesState,
  selectRidesState,
  selectShiftsState,
  selectCustomersState
} from '../redux/slices/appDataSlice';
import appDataManager from '../services/AppDataManager';

export const useAppDataManager = () => {
  const dispatch = useDispatch();
  
  // Manager Status
  const isActive = useSelector(selectIsManagerActive);
  const loadingStates = useSelector(selectAllLoadingStates);
  const errors = useSelector(selectAllErrors);
  
  // Manager starten
  const startManager = useCallback(async () => {
    try {
      const cleanup = await dispatch(startAppDataManager());
      return cleanup;
    } catch (error) {
      console.error('useAppDataManager: Fehler beim Start:', error);
      throw error;
    }
  }, [dispatch]);
  
  // Manager stoppen
  const stopManager = useCallback(async () => {
    try {
      await dispatch(stopAppDataManager());
    } catch (error) {
      console.error('useAppDataManager: Fehler beim Stopp:', error);
      throw error;
    }
  }, [dispatch]);
  
  // Status abrufen
  const getManagerStatus = useCallback(() => {
    return appDataManager.getStatus();
  }, []);
  
  return {
    // Status
    isActive,
    loadingStates,
    errors,
    
    // Actions
    startManager,
    stopManager,
    getManagerStatus,
    
    // Utility
    isAnyLoading: Object.values(loadingStates).some(loading => loading),
    hasAnyError: Object.values(errors).some(error => error !== null)
  };
};

// Hook für einzelne Datentypen (Kompatibilität mit bestehenden Komponenten)
export const useAppData = (dataType) => {
  // Alle Selektoren MÜSSEN immer aufgerufen werden (React Hooks Rules)
  const driversState = useSelector(selectDriversState);
  const vehiclesState = useSelector(selectVehiclesState);
  const ridesState = useSelector(selectRidesState);
  const shiftsState = useSelector(selectShiftsState);
  const customersState = useSelector(selectCustomersState);
  
  // Dann erst die Auswahl basierend auf dataType
  switch (dataType) {
    case 'drivers':
      return driversState;
    case 'vehicles':
      return vehiclesState;
    case 'rides':
      return ridesState;
    case 'shifts':
      return shiftsState;
    case 'customers':
      return customersState;
    default:
      throw new Error(`useAppData: Unbekannter Datentyp: ${dataType}`);
  }
};

// Spezifische Hooks für einfachere Nutzung
export const useDrivers = () => {
  const drivers = useSelector(selectDrivers);
  const isLoading = useSelector(state => state.appData.isLoading.drivers);
  const error = useSelector(state => state.appData.error.drivers);
  
  return { drivers, isLoading, error };
};

export const useVehicles = () => {
  const vehicles = useSelector(selectVehicles);
  const isLoading = useSelector(state => state.appData.isLoading.vehicles);
  const error = useSelector(state => state.appData.error.vehicles);
  
  return { vehicles, isLoading, error };
};

export const useRides = () => {
  const rides = useSelector(selectRides);
  const isLoading = useSelector(state => state.appData.isLoading.rides);
  const error = useSelector(state => state.appData.error.rides);
  
  return { rides, isLoading, error };
};

export const useShifts = () => {
  const shifts = useSelector(selectShifts);
  const isLoading = useSelector(state => state.appData.isLoading.shifts);
  const error = useSelector(state => state.appData.error.shifts);
  
  return { 
    shifts, 
    data: shifts, // Alias für ShiftCalendar Kompatibilität
    isLoading, 
    error 
  };
};

export const useCustomers = () => {
  const customers = useSelector(selectCustomers);
  const isLoading = useSelector(state => state.appData.isLoading.customers);
  const error = useSelector(state => state.appData.error.customers);
  
  return { customers, isLoading, error };
};

// Hook für alle Daten gleichzeitig
export const useAllAppData = () => {
  const drivers = useSelector(selectDrivers);
  const vehicles = useSelector(selectVehicles);
  const rides = useSelector(selectRides);
  const shifts = useSelector(selectShifts);
  const customers = useSelector(selectCustomers);
  const loadingStates = useSelector(selectAllLoadingStates);
  const errors = useSelector(selectAllErrors);
  
  return {
    data: {
      drivers,
      vehicles,
      rides,
      shifts,
      customers
    },
    loading: loadingStates,
    errors,
    isAnyLoading: Object.values(loadingStates).some(loading => loading),
    hasAnyError: Object.values(errors).some(error => error !== null)
  };
};

export default useAppDataManager;